import { useState, useRef, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSiteConfig } from "../config/SiteConfigContext";
import { SUPPORTED_LOCALES, type Locale, saveLocale } from "../app/locale";
import { translatePath } from "../app/paths";

const LOCALE_LABELS: Record<Locale, string> = {
  it: "Italiano",
  en: "English",
  de: "Deutsch",
};

const LOCALE_SHORT: Record<Locale, string> = {
  it: "IT",
  en: "EN",
  de: "DE",
};

/* ─── Animated SVG flag for each locale ─── */
function Flag({ locale, size = 20, animate = false }: { locale: Locale; size?: number; animate?: boolean }) {
  const animDur = "1.8s";
  const s = size;

  switch (locale) {
    case "it":
      return (
        <svg width={s} height={s} viewBox="0 0 60 60" style={{ borderRadius: 4, overflow: "hidden" }}>
          <rect width="20" height="60" x="0" fill="#009246">
            {animate && <animate attributeName="x" from="-20" to="0" dur="0.4s" fill="freeze" />}
          </rect>
          <rect width="20" height="60" x="20" fill="#fff">
            {animate && <animate attributeName="height" from="0" to="60" dur="0.5s" fill="freeze" />}
          </rect>
          <rect width="20" height="60" x="40" fill="#CE2B37">
            {animate && <animate attributeName="x" from="60" to="40" dur="0.4s" fill="freeze" />}
          </rect>
        </svg>
      );

    case "en":
      return (
        <svg width={s} height={s} viewBox="0 0 60 60" style={{ borderRadius: 4, overflow: "hidden" }}>
          <rect width="60" height="60" fill="#012169" />
          {/* Diagonals */}
          <path d="M0,0 L60,60 M60,0 L0,60" stroke="#fff" strokeWidth="8">
            {animate && <animate attributeName="stroke-dasharray" from="0 200" to="200 0" dur={animDur} fill="freeze" />}
          </path>
          <path d="M0,0 L60,60 M60,0 L0,60" stroke="#C8102E" strokeWidth="4">
            {animate && <animate attributeName="stroke-dasharray" from="0 200" to="200 0" dur={animDur} fill="freeze" />}
          </path>
          {/* Cross */}
          <rect width="60" height="10" y="25" fill="#fff">
            {animate && <animate attributeName="width" from="0" to="60" dur="0.5s" fill="freeze" />}
          </rect>
          <rect width="10" height="60" x="25" fill="#fff">
            {animate && <animate attributeName="height" from="0" to="60" dur="0.5s" fill="freeze" />}
          </rect>
          <rect width="60" height="6" y="27" fill="#C8102E">
            {animate && <animate attributeName="width" from="0" to="60" dur="0.6s" fill="freeze" />}
          </rect>
          <rect width="6" height="60" x="27" fill="#C8102E">
            {animate && <animate attributeName="height" from="0" to="60" dur="0.6s" fill="freeze" />}
          </rect>
        </svg>
      );

    case "de":
      return (
        <svg width={s} height={s} viewBox="0 0 60 60" style={{ borderRadius: 4, overflow: "hidden" }}>
          <rect width="60" height="20" y="0" fill="#000">
            {animate && <animate attributeName="y" from="-20" to="0" dur="0.35s" fill="freeze" />}
          </rect>
          <rect width="60" height="20" y="20" fill="#DD0000">
            {animate && <animate attributeName="width" from="0" to="60" dur="0.5s" fill="freeze" />}
          </rect>
          <rect width="60" height="20" y="40" fill="#FFCC00">
            {animate && <animate attributeName="y" from="60" to="40" dur="0.35s" fill="freeze" />}
          </rect>
        </svg>
      );

  }
}

/**
 * @param dropUp apre l'elenco verso l'alto invece che verso il basso.
 *   Serve nel menu mobile, dove lo switcher sta in fondo al pannello laterale:
 *   aprendosi verso il basso l'elenco finiva fuori dallo schermo e le lingue
 *   erano di fatto irraggiungibili.
 */
export function LanguageSwitcher({ dropUp = false }: { dropUp?: boolean }) {
  const { locale } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const cfg = useSiteConfig();
  const [open, setOpen] = useState(false);
  const [animating, setAnimating] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const currentLocale = (locale || "it") as Locale;

  function switchLocale(newLocale: Locale) {
    if (newLocale === currentLocale) {
      setOpen(false);
      return;
    }
    saveLocale(newLocale);
    i18n.changeLanguage(newLocale);
    // Non basta scambiare il prefisso di lingua: gli slug sono tradotti, quindi
    // va tradotto anche il resto del percorso. Altrimenti da /it/collezioni si
    // finiva su /de/collezioni — parole italiane in un indirizzo tedesco.
    // Query e frammento restano: sulla pagina di esito del pagamento
    // `?order_id=` è l'unico riferimento all'ordine (R-70).
    navigate(translatePath(location.pathname, newLocale) + location.search + location.hash, { replace: true });
    setOpen(false);
  }

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Animazione: si accende all'apertura (nel click) e si spegne 300 ms dopo la chiusura.
  useEffect(() => {
    if (open) return;
    const t = setTimeout(() => setAnimating(false), 300);
    return () => clearTimeout(t);
  }, [open]);

  const toggleOpen = () => {
    if (!open) setAnimating(true);
    setOpen(!open);
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      {/* Trigger button */}
      <button
        onClick={toggleOpen}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          background: "none",
          border: "1px solid rgba(28,75,59,0.89)",
          borderRadius: 8,
          padding: "6px 12px 6px 8px",
          cursor: "pointer",
          transition: "border-color 0.2s, background 0.2s",
          borderColor: open ? cfg.color_dark : "rgba(28,75,59,0.89)",
        }}
        onMouseEnter={(e) => {
          if (!open) e.currentTarget.style.borderColor = "#ccc";
        }}
        onMouseLeave={(e) => {
          if (!open) e.currentTarget.style.borderColor = "rgba(28,75,59,0.89)";
        }}
      >
        <Flag locale={currentLocale} size={18} />
        <span
          style={{
            fontSize: 12,
            fontWeight: 500,
            letterSpacing: 0.5,
            color: cfg.color_dark,
          }}
        >
          {LOCALE_SHORT[currentLocale]}
        </span>
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          fill="none"
          stroke={cfg.color_muted}
          strokeWidth="1.5"
          strokeLinecap="round"
          style={{
            transition: "transform 0.25s cubic-bezier(0.16,1,0.3,1)",
            // La freccia indica sempre il verso in cui si aprirà l'elenco.
            transform: open === dropUp ? "rotate(0deg)" : "rotate(180deg)",
          }}
        >
          <path d="M2 3.5L5 6.5L8 3.5" />
        </svg>
      </button>

      {/* Dropdown */}
      {(open || animating) && (
        <div
          style={{
            position: "absolute",
            // Ancorato sopra o sotto il pulsante a seconda dello spazio disponibile.
            ...(dropUp ? { bottom: "calc(100% + 8px)" } : { top: "calc(100% + 8px)" }),
            right: 0,
            background: cfg.color_body_bg,
            border: "1px solid rgba(28,75,59,0.89)",
            borderRadius: 8,
            padding: "6px 0",
            minWidth: 180,
            zIndex: 200,
            boxShadow: dropUp
              ? "0 -8px 32px rgba(0,0,0,0.08), 0 -2px 8px rgba(0,0,0,0.04)"
              : "0 8px 32px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)",
            opacity: open ? 1 : 0,
            // L'elenco entra dal lato da cui si apre.
            transform: open ? "translateY(0) scale(1)" : `translateY(${dropUp ? 8 : -8}px) scale(0.97)`,
            transition: "opacity 0.2s cubic-bezier(0.16,1,0.3,1), transform 0.2s cubic-bezier(0.16,1,0.3,1)",
            pointerEvents: open ? "auto" : "none",
          }}
        >
          {SUPPORTED_LOCALES.map((loc, i) => {
            const isActive = loc === currentLocale;
            return (
              <button
                key={loc}
                onClick={() => switchLocale(loc)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  width: "100%",
                  padding: "10px 16px",
                  background: isActive ? cfg.color_light_bg : "transparent",
                  border: "none",
                  cursor: "pointer",
                  transition: "background 0.15s",
                  opacity: open ? 1 : 0,
                  transform: open ? "translateX(0)" : "translateX(-8px)",
                  transitionDelay: open ? `${i * 40}ms` : "0ms",
                  transitionProperty: "background, opacity, transform",
                  transitionDuration: "0.15s, 0.3s, 0.3s",
                  transitionTimingFunction: "ease, cubic-bezier(0.16,1,0.3,1), cubic-bezier(0.16,1,0.3,1)",
                }}
                onMouseEnter={(e) => {
                  if (!isActive) e.currentTarget.style.background = cfg.color_light_bg;
                }}
                onMouseLeave={(e) => {
                  if (!isActive) e.currentTarget.style.background = "transparent";
                }}
              >
                <Flag locale={loc} size={20} animate={open} />
                <div style={{ flex: 1, textAlign: "left" }}>
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: isActive ? 500 : 400,
                      color: cfg.color_dark,
                      display: "block",
                      lineHeight: 1.3,
                    }}
                  >
                    {LOCALE_LABELS[loc]}
                  </span>
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 500,
                      letterSpacing: 1.5,
                      color: cfg.color_muted,
                      textTransform: "uppercase",
                    }}
                  >
                    {LOCALE_SHORT[loc]}
                  </span>
                </div>
                {isActive && (
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={cfg.color_dark}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
